import { Dispatch, FC, SetStateAction, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';

import { EmailField, SuccessStep } from '@mopla/constants';

import { Input } from '../../atoms/input/input';
import { MplButton } from '../../atoms/MplButton/MplButton';
import { ArrowRightIcon } from '../../icons/ArrowRight';
import { AttentionIcon } from '../../icons/Attention';
import { VerificationAnim } from '../../molecules/Animations';
import { EmailSentWarningMessage } from '../EmailSentWarningMessage/EmailSentWarningMessage';
import { MplErrorModal } from '../MplModal/MplErrorModal';

import {
	Description,
	Footer,
	Root,
	SubmitLevel,
	TextFooter,
	TextFooterLink,
	Title,
} from './DriverForgotPasswordForm.styles';
import DriverForgotPasswordValidationSchema from './DriverForgotPasswordValidation';

interface IFormInput {
	[EmailField.email]: string;
}

interface DriverForgotPasswordProps {
	onSubmit: (values: IFormInput) => Promise<void>;
	onSuccessCallback: () => void;
	successStep: SuccessStep | string;
	error: boolean;
	closeError: VoidFunction;
	setForgotPasswordEmail: Dispatch<SetStateAction<string>>;
	forgotPasswordEmail: string;
}

export const DriverForgotPasswordForm: FC<DriverForgotPasswordProps> = ({
	onSubmit,
	onSuccessCallback,
	successStep,
	error,
	closeError,
	forgotPasswordEmail,
	setForgotPasswordEmail,
}) => {
	const { t } = useTranslation(['forgotPassword', 'validation']);
	const emailForSignIn = localStorage.getItem('emailForSignIn');
	const [isWarningMessageVisible, setIsWarningMessageVisible] =
		useState<boolean>(false);

	const methods = useForm<IFormInput>({
		defaultValues: {
			[EmailField.email]: emailForSignIn || '',
		},
		resolver: yupResolver(DriverForgotPasswordValidationSchema),
		mode: 'onChange',
	});
	const { formState, handleSubmit } = methods;

	const handleFormSubmit = async (data: IFormInput) => {
		setForgotPasswordEmail(data[EmailField.email]);
		await onSubmit(data);
	};

	const checkResend = () => {
		if (
			new Date().valueOf() - Number(localStorage.getItem('emailSendTime')) <=
			60000
		) {
			return setIsWarningMessageVisible(true);
		}
		return onSuccessCallback();
	};

	if (successStep === SuccessStep.forgotPasswordEmailSent) {
		return (
			<div data-testid="forgot-password-success-screen">
				<EmailSentWarningMessage
					open={isWarningMessageVisible}
					onClose={() => setIsWarningMessageVisible(false)}
					email={forgotPasswordEmail}
				/>
				<Box my={6}>
					<VerificationAnim />
				</Box>

				<Title>
					{t('success_screen.text.start', { email: forgotPasswordEmail })}
				</Title>
				<Title>{t('success_screen.text.description')}</Title>

				<SubmitLevel>
					<Footer>
						<TextFooter>{t('success_screen.text.no_email')}</TextFooter>
						<TextFooterLink onClick={checkResend}>
							{t('success_screen.text.again')}
						</TextFooterLink>
					</Footer>
				</SubmitLevel>
			</div>
		);
	}

	return (
		<>
			<Title>{t('text.title')}</Title>
			<FormProvider {...methods}>
				<MplErrorModal
					testId="ForgotPasswordForm-MplErrorModal"
					open={error}
					buttons={
						<MplButton fullWidth onClick={closeError}>
							{t('errors:button.close')}
						</MplButton>
					}
				/>
				<form onSubmit={handleSubmit(handleFormSubmit)}>
					<Root>
						<Controller
							name="email"
							render={({ field }) => (
								<Input
									{...field}
									onChange={(e) => {
										e.target.value = e.target.value.trim();
										field.onChange(e);
									}}
									label={t('label.email')}
									ref={null}
									helperText={
										Boolean(formState.errors.email?.message) && (
											<>
												<AttentionIcon width={15} height={15} />
												<span>
													{t(`validation:${formState.errors.email?.message}`)}
												</span>
											</>
										)
									}
									error={Boolean(formState.errors.email?.message)}
									inputProps={{
										'data-testid': 'forgot-password-email-textField',
									}}
								/>
							)}
						/>
						<Description>{t('text.description')}</Description>
						<SubmitLevel>
							<MplButton
								fullWidth
								endIcon={<ArrowRightIcon />}
								data-testid="forgot-password-submit-button"
								disabled={!formState.isValid}
								type="submit"
							>
								{t('button.send_email')}
							</MplButton>
						</SubmitLevel>
					</Root>
				</form>
			</FormProvider>
		</>
	);
};
